import React, { useEffect, useRef, useState } from 'react';
import { Slider, Tooltip, ConfigProvider } from 'antd';

interface ARVLTVSliderProps {
  ltv: number;
  min_ltv: number;
  max_ltv: number;
  setLtv: (value: number) => void;
  maxLTVPosition: string | null;
  setMaxLTVPosition: (value: string | null) => void;
}

export const ARVLTVSlider = ({ 
  ltv, 
  min_ltv,
  max_ltv, 
  setLtv: _setLtv, 
  maxLTVPosition, 
  setMaxLTVPosition 
}: ARVLTVSliderProps) => {
  let adjustedMinLtv = Math.ceil(min_ltv * 100)
  let adjustedMaxLtv = max_ltv * 100
  let adjustedLtv = Math.ceil(ltv * 100)

  const setLtv = (v: number) => {
    if (v >= adjustedMaxLtv) {
      v = adjustedMaxLtv
    }

    if (v <= adjustedMinLtv) {
      v = adjustedMinLtv
    }

    _setLtv(v/100)
  }

  const [innerLTV, _setInnerLTV] = useState(adjustedLtv)

  const setInnerLTV = (v: number) => {
    v = Math.round(v / 5) * 5

    if (v >= Math.ceil(adjustedMaxLtv)) {
      v = Math.ceil(adjustedMaxLtv)
    }

    if (v <= adjustedMinLtv) {
      v = adjustedMinLtv
    }

    _setInnerLTV(v)
  }

  const node = useRef<HTMLDivElement>(null)
  
  const [isPositioned, setIsPositioned] = useState(false)

  useEffect(() => {
    if (node.current) {
      let tooltipNode = document.querySelector('.page-content-wrapper')?.querySelector('.light.arv-tooltip') as HTMLElement
      let sliderHandleNode = node.current?.querySelector('.ant-slider-handle') as HTMLElement
      let intervalId: NodeJS.Timeout | null = null

      const watcher = async () => {
        intervalId = setInterval(async () => {
          if (tooltipNode) {
            if (tooltipNode && sliderHandleNode) {
              if (!maxLTVPosition) {
                let sliderHandleNodeLeft = window.getComputedStyle(sliderHandleNode).getPropertyValue('left');
                const newMaxLTVPosition = `${parseInt(sliderHandleNodeLeft) - 41}px`;
                tooltipNode.style.left = newMaxLTVPosition
                tooltipNode.style.visibility = "visible"
                setMaxLTVPosition(newMaxLTVPosition)
              } else {
                tooltipNode.style.left = maxLTVPosition;
                tooltipNode.style.visibility = "visible"
              }
              setIsPositioned(true)
            }
            if (intervalId) clearInterval(intervalId);
          } else {
            tooltipNode = document.querySelector('.page-content-wrapper')?.querySelector('.light.arv-tooltip') as HTMLElement
            sliderHandleNode = node.current?.querySelector('.ant-slider-handle') as HTMLElement
          }
        }, 200);
      };

      watcher();

      return () => {
        if (intervalId) {
          clearInterval(intervalId)
        }
      }
    }
  }, [maxLTVPosition, setMaxLTVPosition])

  return (
    <div className='arv-slider' style={{ visibility: (isPositioned ? "visible" : "hidden") }} ref={node}>
      <span>Desired as-is LTV</span>
      <div style={{ padding: "4px 0px" }}>
        <ConfigProvider
          theme={{
            components: {
              Tooltip: {
                colorBgSpotlight: "#355C7D",
                colorTextLightSolid: "#F5FAFF",
                fontFamily: "Inter",
                fontSize: 10,
              }
            },
          }}
        >
          <Tooltip
            zIndex={0}
            placement="topRight"
            title={`Max LTV: ${Math.ceil(adjustedMaxLtv)}%`}
            open
            autoAdjustOverflow={true}
            classNames={{ root: 'light arv-tooltip' }}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
          >
            <ConfigProvider
              theme={{
                components: {
                  Slider: {
                    trackBg: "#355C7D",
                    trackHoverBg: "#355C7D",
                    trackBgDisabled: "#CCD3DB",
                    handleActiveColor: "#355C7D",
                    handleColor: "#355C7D",
                    handleLineWidth: 3,
                    handleLineWidthHover: 4,
                    handleSize: 10,
                    handleSizeHover: 12,
                  }
                },
              }}
            >
              <Slider
                min={0}
                max={100}
                value={innerLTV}
                onChange={setInnerLTV}
                onAfterChange={setLtv}
                tooltip={{ 
                  open: true, 
                  placement: "bottom", 
                  formatter: (v) => `${v}%`, 
                  autoAdjustOverflow: false,
                  zIndex: 1
                }}
              />
            </ConfigProvider>
          </Tooltip>
        </ConfigProvider>
      </div>
    </div>
  )
}

